import React, { useEffect, useState, useRef } from "react";
import { Dropdown, Flex, Text } from "@fluentui/react-northstar";
import styles from "./taskDetailsItem.module.scss";
import pl from "shared/messages/pl";
import TaskDetailsItem from "./TaskDetailsItem";
import EditConfirmationButtons, { EditConfirmationButtonsTypes } from "./EditConfirmationButtons";
import classNames from "classnames";
import { ResponsiveDialog } from "ecom-teams-apps-core";
import { cloneDeep } from "lodash";

interface TaskDetailsDropdownItemProps {
    label: string;
    items: string[];
    selectedValue?: string;
    defaultItemIndex?: number;
    secondaryText?: string;
    isConfirmationRequired?: boolean;
    confirmationMessage?: string;
    canBeChangedFromOutside?: boolean;
    isBusy?: boolean;
    isMobile?: boolean;
    hidden?: boolean;

    multiselect?: boolean;
    selectedValues?: string[];
    editModeButtons?: EditConfirmationButtonsTypes[];

    onChange?: (index: number) => Promise<boolean>;
    onItemsChange?: (values: string[]) => Promise<boolean>;
}

const TaskDetailsDropdownItem: React.FC<TaskDetailsDropdownItemProps> = (props: TaskDetailsDropdownItemProps) => {
    const [isInEditMode, setIsInEditMode] = useState(false);
    const [isBusy, setIsBusy] = useState(props.isBusy);
    const [isConfirmDialogVisible, setIsConfirmDialogVisible] = useState(false);
    const [secondaryText, setSecondaryText] = useState(props.secondaryText);
    const [highlightedItemIndex, setHighlightedItemIndex] = useState<number>();
    const [currentlySelectedItem, setCurrentlySelectedItem] = useState(
        props.defaultItemIndex !== undefined ? props.items[props.defaultItemIndex] : undefined
    );
    const [selectedItems, setSelectedItems] = useState<string[]>(props.selectedValues ?? []);
    const [workSetSelectedItems, setWorkSetSelectedItems] = useState<string[]>([]);
    const [opened, setOpened] = useState<boolean>(false);

    useEffect(() => {
        if (props.canBeChangedFromOutside) {
            setCurrentlySelectedItem(props.items[props.defaultItemIndex!]);
            setSecondaryText(props.secondaryText);
            setIsBusy(props.isBusy);
        }
    }, [props.canBeChangedFromOutside, props.items, props.defaultItemIndex, props.secondaryText, props.isBusy]);

    useEffect(() => {
        if (isInEditMode === true) {
            inputRef.current?.focus();
        }
    }, [isInEditMode]);

    useEffect(() => {
        applyChange();
    }, [highlightedItemIndex]);

    const inputRef = useRef<HTMLInputElement>(null);

    const dataChanged = async () => {
        setIsBusy(true);

        if (props.multiselect && props.onItemsChange) {
            setSelectedItems(workSetSelectedItems);
            await props.onItemsChange(workSetSelectedItems);
        } else if (props.onChange && (await props.onChange(highlightedItemIndex!))) {
            setCurrentlySelectedItem(props.items[highlightedItemIndex!]);
        }

        setIsBusy(false);
    };

    const applyChange = async () => {
        if (
            highlightedItemIndex !== undefined &&
            highlightedItemIndex !== null &&
            highlightedItemIndex !== props.defaultItemIndex!
        ) {
            if (props.isConfirmationRequired) {
                setIsConfirmDialogVisible(true);
                return;
            }

            dataChanged();
        }

        closeEditMode();
    };

    const closeEditMode = async () => {
        setIsConfirmDialogVisible(false);
        setIsInEditMode(false);
        setHighlightedItemIndex(undefined);
        setOpened(false);
    };

    const openEditMode = () => {
        setOpened(true);
        setIsInEditMode(true);
        setWorkSetSelectedItems(cloneDeep(selectedItems));
    };

    if (props.hidden) {
        return null;
    }

    return !isInEditMode ? (
        <TaskDetailsItem
            onClick={() => {
                openEditMode();
            }}
            isLoading={isBusy}
            label={props.label}
            primaryText={currentlySelectedItem ?? pl.taskDetails.dropdown.placeholder}
            primaryTextColor="brand"
            primaryTextWeight="semibold"
            secondaryText={secondaryText}
            isMobile={props.isMobile}
            isMultiline={props.multiselect}
            values={selectedItems}
        />
    ) : (
        <Flex className={classNames({ [styles.wrapper]: props.isMobile })}>
            <Flex className={styles.label}>
                <Text content={props.label} weight="semibold" />
            </Flex>
            <Flex column>
                <Dropdown
                    checkable
                    defaultValue={
                        props.multiselect
                            ? workSetSelectedItems
                            : props.defaultItemIndex !== undefined
                            ? props.items[props.defaultItemIndex]
                            : undefined
                    }
                    className={props.multiselect ? styles.multiselectDropdown : styles.dropdown}
                    fluid
                    onChange={(_, data) => {
                        if (props.multiselect) {
                            setWorkSetSelectedItems(data.value as string[]);
                        } else if (data.highlightedIndex !== null && data.highlightedIndex !== undefined) {
                            setHighlightedItemIndex(data.highlightedIndex);
                            setOpened(false);
                        }
                    }}
                    search
                    items={props.items}
                    noResultsMessage={
                        props.multiselect && props.items.length > 0 ? undefined : pl.taskDetails.dropdown.noResults
                    }
                    open={opened}
                    onFocus={() => setOpened(true)}
                    onBlur={() => {
                        setOpened(false);
                    }}
                    searchInput={{ inputRef: inputRef }}
                    multiple={props.multiselect}
                    offset={({ popper }) => [-popper.width / 4, 0]}
                />
                {props.editModeButtons ? (
                    <EditConfirmationButtons
                        onAccept={() => {
                            dataChanged();
                            closeEditMode();
                        }}
                        onCancel={() => {
                            closeEditMode();
                        }}
                        buttons={props.editModeButtons}
                    />
                ) : null}
                <ResponsiveDialog
                    cancelButton={pl.common.close}
                    confirmButton={pl.common.confirm}
                    onConfirm={async () => {
                        dataChanged();
                        closeEditMode();
                    }}
                    onCancel={() => {
                        closeEditMode();
                    }}
                    header={pl.taskDetails.taskDetailsDropdownConfirmationDialog.header}
                    content={props.confirmationMessage}
                    open={isConfirmDialogVisible}
                    isMobile={props.isMobile}
                    gap={"gap.small"}
                />
            </Flex>
        </Flex>
    );
};

export default TaskDetailsDropdownItem;
